import { call, put, takeLatest } from 'redux-saga/effects';
import {
	TDateRange,
	TDetailedPolygons,
	TVesselCurrentStatus,
	TVesselInfo,
	actionTypes
} from './mapTypes';
import { getVesselsLastSignalErrorAction, getVesselsLastSignalSuccessAction } from './slices/vesselsLastSignalSlice';
import { getVesselLastVoyagesErrorAction, getVesselLastVoyagesSuccessAction } from './slices/vesselLastVoyagesSlice';
import { getVesselLastStoppageEventsErrorAction, getVesselLastStoppageEventsSuccessAction } from './slices/vesselLastStoppageEventsSlice';
import { getVesselLastPortCallsErrorAction, getVesselLastPortCallsSuccessAction } from './slices/vesselLastPortCallsSlice';
import { PayloadAction } from '@reduxjs/toolkit';
import { handleAxiosError } from '../../utils/helpers';
import { trackEvent, trackExceptionWithContext } from '../../services/application-insights/appInsights';
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from '../../services/application-insights/appInsightsConstants';
import {
	getDateRange,
	getDetailedPolygons,
	getVesselCurrentStatusByImoNumber,
	getVesselDailyPositionsByImoNumbers,
	getVesselLastPortCallsByImoNumber,
	getVesselLastStoppageEventsByImoNumber,
	getVesselLastVoyagesByImoNumbers,
	getVesselOngoingVoyageDailySignalsByImoNumbers,
	getVesselOngoingVoyageSignalsByImoNumber,
	getVesselsLastSignalByImoNumbers,
	getVesselVoyageDailySignalsByImoNumbers
} from './mapApi';
import { getVesselOngoingVoyageSignalsErrorAction, getVesselOngoingVoyageSignalsSuccessAction } from './slices/vesselOngoingVoyageSignalsSlice';
import { getVesselVoyageDailySignalsErrorAction, getVesselVoyageDailySignalsSuccessAction } from './slices/vesselVoyageDailySignalsSlice';
import { getVesselDailyPositionsErrorAction, getVesselDailyPositionsSuccessAction } from './slices/vesselDailyPositionsSlice';
import { getVesselOngoingVoyageDailySignalsErrorAction, getVesselOngoingVoyageDailySignalsSuccessAction } from './slices/vesselOngoingVoyageDailySignalsSlice';
import { getDefaultActivityDateRangeErrorAction, getDefaultActivityDateRangeSuccessAction } from './slices/defaultActivityDateRangeSlice';
import { detailedPolygonsActions } from './slices/detailedPolygonsSlice';
import { getVesselCurrentStatusErrorAction, getVesselCurrentStatusSuccessAction } from './slices/vesselCurrentStatusSlice';

function* getVesselsLastSignalByImoNumbersSaga({ payload: { imoList, date, yearMonth } }: PayloadAction<{ imoList: number[], date: string, yearMonth: number }>) {
	const sagaId = `vessels-last-signal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	
	try {
		trackEvent(AppInsightsActions.MAP_DATA_FETCH_ATTEMPT, {
			component: AppInsightsComponents.MAP_SAGA,
			action: 'vessels-last-signal-fetch',
			imoCount: imoList.length,
			imoList: imoList.join(','),
			date,
			yearMonth: yearMonth.toString(),
			sagaId
		});

		const response: TVesselInfo = yield call(getVesselsLastSignalByImoNumbers, imoList, date, yearMonth);

		trackEvent(AppInsightsActions.MAP_DATA_FETCH_SUCCESS, {
			component: AppInsightsComponents.MAP_SAGA,
			action: 'vessels-last-signal-success',
			featureCount: response?.features?.length || 0,
			responseType: response?.type || 'unknown',
			sagaId
		});

		yield put(getVesselsLastSignalSuccessAction(response));
	} catch (error: any) {
		trackExceptionWithContext(error, {
			component: AppInsightsComponents.MAP_SAGA,
			action: 'vessels-last-signal-error',
			additionalData: {
				errorType: AppInsightsErrorTypes.MAP_DATA_ERROR,
				imoList: imoList.join(','),
				date,
				yearMonth: yearMonth.toString(),
				sagaId
			}
		});
		yield put(getVesselsLastSignalErrorAction(handleAxiosError(error)));
	}
}

function* getVesselLastVoyagesByImoNumbersSaga({ payload: { imoNumbers, startDate, endDate } }: PayloadAction<{ imoNumbers: number[], startDate: string, endDate: string }>) {
	const sagaId = `vessel-voyages-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	
	try {
		trackEvent(AppInsightsActions.MAP_DATA_FETCH_ATTEMPT, {
			component: AppInsightsComponents.MAP_SAGA,
			action: 'vessel-last-voyages-fetch',
			imoCount: imoNumbers.length,
			imoNumbers: imoNumbers.join(','),
			startDate,
			endDate,
			sagaId
		});

		const response: TVesselInfo = yield call(getVesselLastVoyagesByImoNumbers, imoNumbers, startDate, endDate);

		trackEvent(AppInsightsActions.MAP_DATA_FETCH_SUCCESS, {
			component: AppInsightsComponents.MAP_SAGA,
			action: 'vessel-last-voyages-success',
			featureCount: response?.features?.length || 0,
			sagaId
		});

		yield put(getVesselLastVoyagesSuccessAction(response));
	} catch (error: any) {
		trackExceptionWithContext(error, {
			component: AppInsightsComponents.MAP_SAGA,
			action: 'vessel-last-voyages-error',
			additionalData: {
				errorType: AppInsightsErrorTypes.MAP_DATA_ERROR,
				imoNumbers: imoNumbers.join(','),
				startDate,
				endDate,
				sagaId
			}
		});
		yield put(getVesselLastVoyagesErrorAction(handleAxiosError(error)));
	}
}

function* getVesselLastStoppageEventsByImoNumberSaga({ payload: { imoNumber, startDate, endDate } }: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) {
	try {
		const response: TVesselInfo = yield call(getVesselLastStoppageEventsByImoNumber, imoNumber, startDate, endDate);

		yield put(getVesselLastStoppageEventsSuccessAction(response));
	} catch (error: any) {
		yield put(getVesselLastStoppageEventsErrorAction(handleAxiosError(error)));
	}
}

function* getVesselLastPortCallsByImoNumberSaga({ payload: { imoNumber, startDate, endDate } }: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) {
	try {
		const response: TVesselInfo = yield call(getVesselLastPortCallsByImoNumber, imoNumber, startDate, endDate);

		yield put(getVesselLastPortCallsSuccessAction(response));
	} catch (error: any) {
		yield put(getVesselLastPortCallsErrorAction(handleAxiosError(error)));
	}
}

function* getVesselOngoingVoyageSignalsByImoNumberSaga({ payload: { imoNumber, date } }: PayloadAction<{ imoNumber: number, date: string }>) {
	try {
		const response: TVesselInfo = yield call(getVesselOngoingVoyageSignalsByImoNumber, imoNumber, date);

		yield put(getVesselOngoingVoyageSignalsSuccessAction(response));
	} catch (error: any) {
		yield put(getVesselOngoingVoyageSignalsErrorAction(handleAxiosError(error)));
	}
}

function* getVesselVoyageDailySignalsByImoNumbersSaga({ payload: { imoNumbers, startDate, endDate } }: PayloadAction<{ imoNumbers: number[], startDate: string, endDate: string }>) {
	try {
		const response: TVesselInfo = yield call(getVesselVoyageDailySignalsByImoNumbers, imoNumbers, startDate, endDate);

		yield put(getVesselVoyageDailySignalsSuccessAction(response));
	} catch (error: any) {
		yield put(getVesselVoyageDailySignalsErrorAction(handleAxiosError(error)));
	}
}

function* getVesselDailyPositionsByImoNumbersSaga({ payload: { imoNumbers, date } }: PayloadAction<{ imoNumbers: number[], date: string }>) {
	try {
		const response: TVesselInfo = yield call(getVesselDailyPositionsByImoNumbers, imoNumbers, date);

		yield put(getVesselDailyPositionsSuccessAction(response));
	} catch (error: any) {
		yield put(getVesselDailyPositionsErrorAction(handleAxiosError(error)));
	}
}

function* getVesselOngoingVoyageDailySignalsByImoNumbersSaga({ payload: { imoNumbers, date } }: PayloadAction<{ imoNumbers: number[], date: string }>) {
	try {
		const response: TVesselInfo = yield call(getVesselOngoingVoyageDailySignalsByImoNumbers, imoNumbers, date);

		yield put(getVesselOngoingVoyageDailySignalsSuccessAction(response));
	} catch (error: any) {
		yield put(getVesselOngoingVoyageDailySignalsErrorAction(handleAxiosError(error)));
	}
}

function* getDateRangeSaga() {
	try {
		const response: TDateRange = yield call(getDateRange);

		yield put(getDefaultActivityDateRangeSuccessAction(response));
	} catch (error: any) {
		yield put(getDefaultActivityDateRangeErrorAction(handleAxiosError(error)));
	}
}

function* getDetailedPolygonsSaga() {
	try {
		const response: TDetailedPolygons = yield call(getDetailedPolygons);

		yield put(detailedPolygonsActions.getDetailedPolygonsSuccessAction(response));
	} catch (error: any) {
		yield put(detailedPolygonsActions.getDetailedPolygonsErrorAction(handleAxiosError(error)));
	}
}

function* getVesselCurrentStatusByImoNumberSaga({ payload: imoNumber }: PayloadAction<number>) {
	const sagaId = `vessel-status-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	
	try {
		trackEvent(AppInsightsActions.MAP_DATA_FETCH_ATTEMPT, {
			component: AppInsightsComponents.MAP_SAGA,
			action: 'vessel-current-status-fetch',
			imoNumber: imoNumber.toString(),
			sagaId
		});

		const response: TVesselCurrentStatus = yield call(getVesselCurrentStatusByImoNumber, imoNumber);

		trackEvent(AppInsightsActions.MAP_DATA_FETCH_SUCCESS, {
			component: AppInsightsComponents.MAP_SAGA,
			action: 'vessel-current-status-success',
			imoNumber: imoNumber.toString(),
			hasStatus: !!response,
			sagaId
		});

		yield put(getVesselCurrentStatusSuccessAction(response));
	} catch (error: any) {
		trackExceptionWithContext(error, {
			component: AppInsightsComponents.MAP_SAGA,
			action: 'vessel-current-status-error',
			additionalData: {
				errorType: AppInsightsErrorTypes.MAP_DATA_ERROR,
				imoNumber: imoNumber.toString(),
				sagaId
			}
		});
		yield put(getVesselCurrentStatusErrorAction(handleAxiosError(error)));
	}
}

export function* watchMapActions(): Generator {
	yield takeLatest(actionTypes.GET_VESSELS_LAST_SIGNAL_REQUEST, getVesselsLastSignalByImoNumbersSaga);
	yield takeLatest(actionTypes.GET_VESSEL_LAST_VOYAGES_REQUEST, getVesselLastVoyagesByImoNumbersSaga);
	yield takeLatest(actionTypes.GET_VESSEL_LAST_STOPPAGE_EVENTS_REQUEST, getVesselLastStoppageEventsByImoNumberSaga);
	yield takeLatest(actionTypes.GET_VESSEL_LAST_PORT_CALLS_REQUEST, getVesselLastPortCallsByImoNumberSaga);
	yield takeLatest(actionTypes.GET_VESSEL_ONGOING_VOYAGE_SIGNALS_REQUEST, getVesselOngoingVoyageSignalsByImoNumberSaga);
	yield takeLatest(actionTypes.GET_VESSEL_VOYAGE_DAILY_SIGNALS_REQUEST, getVesselVoyageDailySignalsByImoNumbersSaga);
	yield takeLatest(actionTypes.GET_VESSEL_DAILY_POSITIONS_REQUEST, getVesselDailyPositionsByImoNumbersSaga);
	yield takeLatest(actionTypes.GET_VESSEL_ONGOING_VOYAGE_DAILY_SIGNALS_REQUEST, getVesselOngoingVoyageDailySignalsByImoNumbersSaga);
	yield takeLatest(actionTypes.GET_DATE_RANGE_REQUEST, getDateRangeSaga);
	yield takeLatest(actionTypes.GET_DETAILED_POLYGONS_REQUEST, getDetailedPolygonsSaga);
	yield takeLatest(actionTypes.GET_VESSEL_CURRENT_STATUS_REQUEST, getVesselCurrentStatusByImoNumberSaga);
}
